import { z } from "zod";
import { MemoryModuleSchema, SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SOCKET_ID_VERSION_CONFIG_KEY } from "../../../utils/dynamicSockets/dynamicSocketId";

/**
 * The kinds of evidence Match Customer With Evidence can weigh. Each has a
 * deterministic matcher; none uses a model.
 *
 * - `customer`: a customer the source already knows (an AR_CUSTOMER ref, or a
 *   provenance-like object or a string carrying the customer id), checked to
 *   belong to the organization.
 * - `inbound_email`: the sender of an INBOUND_EMAIL ref (the original sender
 *   for an internal forward), looked up in the configured area's sender
 *   memory (`memoryModule`) by address, then by customer-specific domain.
 * - `email_address`: a plain sender address, looked up the same way.
 * - `invoice_numbers`: invoice numbers, matched to the one customer that owns
 *   them (a number on several invoices counts for nothing; owners that
 *   disagree match nobody).
 */
export const CUSTOMER_EVIDENCE_KINDS = [
  "customer",
  "inbound_email",
  "email_address",
  "invoice_numbers",
] as const;

export type CustomerEvidenceKind = (typeof CUSTOMER_EVIDENCE_KINDS)[number];

/** One configured piece of evidence; each becomes one input socket. */
export const customerEvidenceEntrySchema = z.object({
  key: z
    .string()
    .regex(
      /^[a-z][a-z0-9_]*$/,
      "Key must be lowercase alphanumeric with underscores",
    )
    .describe("Input socket key"),
  label: z.string().optional().describe("Display label for the socket"),
  kind: z
    .enum(CUSTOMER_EVIDENCE_KINDS)
    .describe("What the evidence is, which decides how it is matched"),
});

export type CustomerEvidenceEntry = z.infer<typeof customerEvidenceEntrySchema>;

/** A piece of evidence that named a different customer than the winner. */
export const customerEvidenceConflictSchema = z.object({
  evidenceKey: z.string(),
  kind: z.enum(CUSTOMER_EVIDENCE_KINDS),
  customerId: z.uuid(),
  reason: z.string(),
});

/**
 * The customer evidence matched before extraction, and where it came from.
 * Identify Customer decides with a `customer`-kind match instead of
 * re-guessing (unless the cited invoices contradict it); sender memory it
 * re-reads at decision time.
 */
export const customerEvidenceMatchSchema = z.object({
  customerId: z.uuid(),
  /** The configured evidence entry that decided. */
  evidenceKey: z.string(),
  kind: z.enum(CUSTOMER_EVIDENCE_KINDS),
  /** Human-readable reason, e.g. `sender ap@acme.com` or `portal intake`. */
  reason: z.string(),
  /** The sender-memory rule that decided it, when one did. */
  ruleId: z.uuid().nullable(),
  ruleVersion: z.int().nullable(),
  /**
   * Later evidence that named a different customer. It never overrides the
   * winner; it is recorded so a person can see the disagreement.
   */
  conflicts: z.array(customerEvidenceConflictSchema),
});

export type CustomerEvidenceMatch = z.infer<typeof customerEvidenceMatchSchema>;

/**
 * Match Customer With Evidence
 *
 * Answers "do we already know whose document this is?" without any model,
 * from the evidence the workflow author wires in: a customer the source knows
 * (a portal binding, the bank receipt's customer, a person submitting for a
 * customer), an email's sender or a sender address in sender memory, or
 * invoice numbers' owner. Evidence is weighed in the configured order and the
 * first that resolves a customer wins; later evidence naming a different
 * customer is recorded as a conflict, never applied. The match limits which
 * extraction hints apply and is handed to Identify Customer.
 */
export const matchCustomerWithEvidenceStep = defineStepConfig({
  type: "match_customer_with_evidence",
  name: "Match Customer With Evidence",
  description:
    "Find a document's customer from configured evidence (a known customer, an email's sender, a sender address, invoice numbers), in order, without a model",
  category: "Data Access",
  libraryModule: "AccountsReceivable",
  // One input socket per configured evidence entry; see
  // generateMatchCustomerWithEvidenceInputSockets.
  inputSchema: z.object({}).passthrough(),
  outputSchema: z.object({
    customer: entityOfType(SystemEntity.AR_CUSTOMER)
      .nullable()
      .describe("The matched customer, or null when no evidence matched one"),
    match: customerEvidenceMatchSchema
      .nullable()
      .describe(
        "Where the customer came from (evidence, reason, rule) and any evidence that disagreed",
      ),
  }),
  configSchema: z.object({
    memoryModule: MemoryModuleSchema.default("remittance").describe(
      "The memory area sender evidence (inbound_email, email_address) is looked up in. Areas never share memory: a mapping learned for remittances is not found from cash receipts or sales orders.",
    ),
    evidence: z
      .array(customerEvidenceEntrySchema)
      .default([])
      .superRefine((entries, ctx) => {
        const seen = new Set<string>();
        entries.forEach((entry, index) => {
          if (seen.has(entry.key)) {
            ctx.addIssue({
              code: "custom",
              message: `Duplicate evidence[].key: ${entry.key}`,
              path: [index, "key"],
            });
          }
          seen.add(entry.key);
        });
      })
      .describe(
        "Evidence to weigh, in order. Each entry becomes an input socket; the first that matches a customer wins.",
      ),
    // Declared so z.object() does not strip the stamp on parse: the handler
    // derives its socket ids from it, like the canvas did.
    [SOCKET_ID_VERSION_CONFIG_KEY]: z
      .number()
      .int()
      .optional()
      .describe(
        "Socket-id scheme this step was created under. Absent means the legacy scheme, where an evidence key IS its socket id.",
      ),
  }),
  emits: [] as const,
});
